import { useEffect, useRef, useState, type ReactNode } from 'react';
import './RichTextEditor.css';

type Mode = 'design' | 'html' | 'preview';

const BLOCKS = [{ v: 'p', t: 'Normal' }, { v: 'h1', t: 'Heading 1' }, { v: 'h2', t: 'Heading 2' }, { v: 'h3', t: 'Heading 3' }, { v: 'h4', t: 'Heading 4' }, { v: 'h5', t: 'Heading 5' }, { v: 'h6', t: 'Heading 6' }];
const FONTS = ['Times New Roman', 'Tahoma', 'Verdana', 'Arial', 'MS Sans Serif', 'Courier', 'Segoe UI'];
// execCommand fontSize is 1–7; labelled like the LIMS HtmlEditor ("1 (8pt)" …).
const SIZES = [{ v: '1', t: '1 (8pt)' }, { v: '2', t: '2 (10pt)' }, { v: '3', t: '3 (12pt)' }, { v: '4', t: '4 (14pt)' }, { v: '5', t: '5 (18pt)' }, { v: '6', t: '6 (24pt)' }, { v: '7', t: '7 (36pt)' }];
// Opt-in `fontSizesPx` mode: real point sizes applied as exact CSS px (the size
// the user picks is the size the text renders), for the Email Template editor.
const PX_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36];
// A fixed, consistent palette for the opt-in colour swatches (never a layout that
// shifts as colours change). White carries a border so it stays visible.
const SWATCHES = ['#000000', '#444444', '#e5484d', '#e8670a', '#f5a623', '#2e7d32', '#1565c0', '#6a1b9a', '#795548', '#ffffff'];

/** Words in the HTML's TEXT — markup is not content, and an untouched editor
 *  still holds "<p><br></p>". */
function wordCount(html: string): number {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  return text ? text.split(/\s+/).length : 0;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /**
   * `full` (default) is the legacy XAF HtmlEditor: font, size, colour, align,
   * strike, clear-formatting, and the Design / HTML / Preview tabs. It is what
   * the Employee résumé and Project Notes have always shown, so it stays the
   * default and those screens are untouched.
   *
   * `simple` is the composition toolbar — block, B/I/U, lists and indent, link,
   * image, table, undo/redo — with no source tabs. For a field where the user
   * writes a paragraph of prose, not one where they hand-tune markup.
   */
  variant?: 'full' | 'simple';
  /** Paints the required-empty tint, matching every other required field. */
  invalid?: boolean;
  /** A note INSIDE the editor's own border, so it reads as part of the control
   *  rather than as loose text under it. */
  footerNote?: string;
  /** Live word count, at the far end of that same footer. */
  showWordCount?: boolean;
  /** Adds Undo / Redo buttons to the (full-variant) toolbar. Opt-in and default
   *  off, so every existing full/simple screen renders exactly as before; only a
   *  caller that asks for them (e.g. the Email Template editor) shows them. */
  showUndoRedo?: boolean;
  /** Opt-in: the size dropdown lists real point sizes (8, 9, 10, … 36) and
   *  applies them as EXACT CSS px, so the picked size is the rendered size.
   *  Default off keeps the legacy 1–7 bucket sizes on every other screen. */
  fontSizesPx?: boolean;
  /** Opt-in: replace the single colour-picker input with a fixed row of colour
   *  swatch boxes (default off keeps the `<input type=color>` on other screens). */
  colorSwatches?: boolean;
  /** Opt-in: the full Word-style toolbar for the Email Template body — grow /
   *  shrink font size, change case, subscript / superscript, text highlight,
   *  justify, multilevel list, line spacing, and live active-state on the toggle
   *  buttons. Implies fontSizesPx + colorSwatches + showUndoRedo. Default off, so
   *  every other full/simple screen keeps its existing toolbar exactly. */
  wordToolbar?: boolean;
  /** Hides the Design / HTML / Preview tab strip, leaving design mode only.
   *  Default off, so every other screen keeps its tabs. */
  hideSourceTabs?: boolean;
  /** Opt-in: what the Preview tab shows instead of the raw HTML — e.g. the Email
   *  Template editor renders the body with real field values from the server.
   *  Called only while Preview is open. Default unset keeps the plain HTML preview. */
  renderPreview?: () => ReactNode;
}

// Word-style colour-panel palettes for the popup pickers (Text / Highlight).
const THEME_COLORS = ['#ffffff', '#000000', '#e7e6e6', '#44546a', '#4472c4', '#ed7d31', '#a5a5a5', '#ffc000', '#5b9bd5', '#70ad47'];
const STANDARD_COLORS = ['#c00000', '#ff0000', '#ffc000', '#ffff00', '#92d050', '#00b050', '#00b0f0', '#0070c0', '#002060', '#7030a0'];

/** A Word-style popup colour panel — an icon button showing the current colour as
 *  a bar, a caret that opens a panel with Automatic / Theme / Standard colours and
 *  a "More Colours…" native picker. Used for both Text Colour and Highlight in the
 *  Email Template body toolbar. Every mousedown preventDefaults so the editor's
 *  text selection survives the click and the colour applies to it. */
function RteColorMenu({ icon, title, autoLabel, autoValue, value, onPick }: {
  icon: string; title: string; autoLabel: string; autoValue: string; value: string; onPick: (c: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const pick = (c: string) => { onPick(c); setOpen(false); };
  const barColor = value === 'transparent' ? '#fff' : value;
  return (
    <span className="rte-cwrap" ref={wrapRef}>
      <button type="button" className="rte-btn rte-cbtn" title={title}
        onMouseDown={(e) => { e.preventDefault(); onPick(value); }}>
        <i className={`ti ${icon}`} />
        <span className="rte-cbar" style={{ background: barColor }} />
      </button>
      <button type="button" className="rte-btn rte-ccaret" title={`${title} options`}
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}>
        <i className="ti ti-chevron-down" />
      </button>
      {open && (
        <div className="rte-cpanel" onMouseDown={(e) => e.preventDefault()}>
          <button type="button" className="rte-cp-auto" onMouseDown={(e) => { e.preventDefault(); pick(autoValue); }}>
            <span className="rte-cp-autobox" style={{ background: autoValue === 'transparent' ? '#fff' : autoValue }} /> {autoLabel}
          </button>
          <div className="rte-cp-sec">Theme Colors</div>
          <div className="rte-cp-grid">{THEME_COLORS.map((c) => (
            <button key={c} type="button" className="rte-cp-sw" style={{ background: c }} title={c} onMouseDown={(e) => { e.preventDefault(); pick(c); }} />
          ))}</div>
          <div className="rte-cp-sec">Standard Colors</div>
          <div className="rte-cp-grid">{STANDARD_COLORS.map((c) => (
            <button key={c} type="button" className="rte-cp-sw" style={{ background: c }} title={c} onMouseDown={(e) => { e.preventDefault(); pick(c); }} />
          ))}</div>
          <button type="button" className="rte-cp-more" onMouseDown={(e) => { e.preventDefault(); nativeRef.current?.click(); }}>
            <i className="ti ti-palette" /> More Colors…
          </button>
          <input ref={nativeRef} type="color" className="rte-cp-native" onChange={(e) => pick(e.target.value)} />
        </div>
      )}
    </span>
  );
}

/**
 * Lightweight rich-text (HTML) editor — mirrors the legacy XAF HtmlEditor: a
 * formatting toolbar over a contentEditable Design area, plus raw HTML and
 * Preview modes. Uses document.execCommand so it needs no extra package
 * dependency (CLAUDE.md §15).
 *
 * Every option below is OPT-IN and defaults to the original behaviour, so the
 * Employee résumé, Project Notes and Users screens keep exactly the control they
 * had before the Client Request form needed a simpler one.
 */
export function RichTextEditor({
  value, onChange, variant = 'full', invalid = false, footerNote, showWordCount = false,
  showUndoRedo = false, fontSizesPx = false, colorSwatches = false, wordToolbar = false,
  hideSourceTabs = false, renderPreview,
}: RichTextEditorProps) {
  const simple = variant === 'simple';
  // wordToolbar is the cohesive Word-style set; it turns on the numeric sizes,
  // colour swatches and undo/redo too, so a caller passes just `wordToolbar`.
  const showSizesPx = fontSizesPx || wordToolbar;
  const showSwatches = colorSwatches || wordToolbar;
  const showUndo = showUndoRedo || wordToolbar;
  const [mode, setMode] = useState<Mode>('design');
  const [block, setBlock] = useState('p');
  const [font, setFont] = useState('Times New Roman');
  // Live active-state of the toggle commands (bold/italic/…) for the Word toolbar.
  const [activeCmds, setActiveCmds] = useState<Record<string, boolean>>({});
  // Last chosen text / highlight colour (drives the toolbar indicator bar + the
  // "click the icon to re-apply" behaviour). Word defaults: red text, yellow hi.
  const [foreColor, setForeColor] = useState('#c00000');
  const [hiliteColor, setHiliteColor] = useState('#ffff00');
  const caseMode = useRef(0);
  const ref = useRef<HTMLDivElement>(null);
  // Clicking a toolbar <select> (font / size / block) or the colour input moves focus OUT of the
  // contentEditable, which collapses the text selection — so the command would apply to nothing.
  // We remember the last selection made inside the editor and restore it before running the command.
  const savedRange = useRef<Range | null>(null);

  // Sync the contentEditable only when the value differs from the live DOM, so
  // typing (which already updated the DOM) never resets the caret position.
  useEffect(() => {
    if (mode === 'design' && ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value;
  }, [value, mode]);

  // Track the last REAL (non-collapsed) selection inside the editor. A caret (collapsed) never
  // overwrites it, so clicking a toolbar <select> can't wipe the highlighted text we want to style.
  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && !sel.isCollapsed && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  // Reflect the selection's current formatting on the Word-toolbar toggle buttons
  // (bold/italic/…): queryCommandState on each, only while the caret is inside the
  // editor. A no-op unless the Word toolbar is on, so other screens are untouched.
  const TOGGLE_CMDS = ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript',
    'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull', 'insertUnorderedList', 'insertOrderedList'];
  const refreshActive = () => {
    if (!wordToolbar) return;
    const sel = window.getSelection();
    if (!sel || !ref.current || !sel.anchorNode || !ref.current.contains(sel.anchorNode)) return;
    const next: Record<string, boolean> = {};
    for (const c of TOGGLE_CMDS) { try { next[c] = document.queryCommandState(c); } catch { /* ignore */ } }
    setActiveCmds(next);
  };
  // selectionchange fires for every selection update — the most reliable capture point.
  useEffect(() => {
    const onSel = () => { saveSel(); refreshActive(); };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const restoreSel = () => {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      try { sel.removeAllRanges(); sel.addRange(savedRange.current); } catch { /* range stale — ignore */ }
    }
  };
  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
  // execCommand-based commands (bold/italic/lists/align/block/link) keep the selection.
  const runCmd = (cmd: string, arg?: string) => {
    ref.current?.focus();
    restoreSel();
    document.execCommand(cmd, false, arg);
    emit();
    saveSel();
  };
  const exec = (cmd: string, arg?: string) => runCmd(cmd, arg);   // buttons keep selection → immediate
  const execSel = (cmd: string, arg?: string) => { window.setTimeout(() => runCmd(cmd, arg), 0); };   // selects steal focus → defer
  // Inline style (font / size / colour). Uses execCommand with CSS output so it REPLACES the existing
  // formatting on the selection (no nested <span> build-up) instead of wrapping again each time.
  // Deferred a tick so the editor regains focus after the <select>/colour-picker; the selection is
  // restored first (tracked reliably via the selectionchange listener above).
  const runStyleCmd = (cmd: string, arg: string) => {
    const el = ref.current; if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !el.contains(sel.anchorNode)) restoreSel();
    try { document.execCommand('styleWithCSS', false, 'true'); } catch { /* ignore */ }
    document.execCommand(cmd, false, arg);
    try { document.execCommand('styleWithCSS', false, 'false'); } catch { /* ignore */ }
    emit();
    saveSel();
  };
  const applyStyle = (cmd: string, arg: string) => { window.setTimeout(() => runStyleCmd(cmd, arg), 0); };
  // Text / highlight colour pickers — remember the choice for the indicator and
  // apply it to the selection (or the caret's upcoming text).
  const pickFore = (c: string) => { setForeColor(c); applyStyle('foreColor', c); };
  const pickHilite = (c: string) => { setHiliteColor(c); applyStyle('hiliteColor', c); };
  // Apply an EXACT px font-size to the selection. execCommand('fontSize') only
  // takes the 1–7 buckets, so we tag the selection with the unused size 7 then
  // rewrite those <font size="7"> wrappers into spans carrying the real px — the
  // standard contentEditable technique for arbitrary sizes, no extra dependency.
  const applyFontSizePx = (px: string) => window.setTimeout(() => {
    const el = ref.current; if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !el.contains(sel.anchorNode)) restoreSel();
    try { document.execCommand('styleWithCSS', false, 'true'); } catch { /* ignore */ }
    document.execCommand('fontSize', false, '7');
    el.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement('span');
      span.style.fontSize = `${px}px`;
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    emit();
    saveSel();
  }, 0);
  const btn = (cmd: string, icon: string, title: string) => (
    <button type="button" className={`rte-btn${activeCmds[cmd] ? ' rte-btn-active' : ''}`} title={title}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd); refreshActive(); }}><i className={`ti ${icon}`} /></button>
  );
  // Grow / shrink the selection to the next / previous point size, read from the
  // selection's live computed size (Word's A˄ / A˅ behaviour). preventDefault on the
  // button keeps the selection, so applyFontSizePx targets it.
  const stepFontSize = (dir: 1 | -1) => {
    const el = ref.current; if (!el) return;
    const sel = window.getSelection();
    let cur = 0;
    if (sel && sel.rangeCount) {
      let n: Node | null = sel.anchorNode;
      if (n && n.nodeType === 3) n = n.parentElement;
      if (n instanceof Element) cur = parseInt(window.getComputedStyle(n).fontSize, 10) || 0;
    }
    if (!cur) cur = 12;
    const target = dir > 0
      ? (PX_SIZES.find((s) => s > cur) ?? PX_SIZES[PX_SIZES.length - 1])
      : ([...PX_SIZES].reverse().find((s) => s < cur) ?? PX_SIZES[0]);
    applyFontSizePx(String(target));
  };
  // Cycle the selected text UPPERCASE → lowercase → Capitalise Each Word. Field
  // tokens ({{SampleId}} …) are left byte-for-byte so their resolution is intact.
  const changeCase = () => {
    const el = ref.current; if (!el) return;
    const sel = window.getSelection();
    const text = sel && !sel.isCollapsed ? sel.toString() : '';
    if (!text) return;
    const mode = caseMode.current % 3; caseMode.current += 1;
    const conv = (s: string) => mode === 0 ? s.toUpperCase() : mode === 1 ? s.toLowerCase()
      : s.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\B\w/g, (c) => c.toLowerCase());
    const out = text.split(/(\{\{[^}]+\}\})/).map((p) => /^\{\{[^}]+\}\}$/.test(p) ? p : conv(p)).join('');
    el.focus(); restoreSel();
    document.execCommand('insertText', false, out);
    emit(); saveSel();
  };
  // Set line / paragraph spacing on the nearest block of the selection.
  const setLineHeight = (lh: string) => {
    const el = ref.current; if (!el) return;
    el.focus(); restoreSel();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node: Node | null = sel.anchorNode;
    if (node && node.nodeType === 3) node = node.parentElement;
    let block: HTMLElement | null = node instanceof HTMLElement ? node : null;
    while (block && block !== el && window.getComputedStyle(block).display !== 'block') block = block.parentElement;
    if (block && block !== el) block.style.lineHeight = lh; else el.style.lineHeight = lh;
    emit(); saveSel();
  };

  /** A 2×2 starter table — enough to type into, and the user can add rows with
   *  Tab the way every editor behaves. */
  const insertTable = () => {
    const cell = '<td style="border:1px solid #d7dbe3;padding:6px">&nbsp;</td>';
    exec('insertHTML',
      `<table style="border-collapse:collapse;width:100%"><tbody>`
      + `<tr>${cell}${cell}</tr><tr>${cell}${cell}</tr></tbody></table><p><br></p>`);
  };

  return (
    <div className={`rte${simple ? ' rte-simple' : ''}${invalid ? ' required-empty' : ''}`}>
      {mode === 'design' && (
        <div className="rte-toolbar">
          <select className="rte-block" title="Paragraph format" value={block} onChange={(e) => { setBlock(e.target.value); execSel('formatBlock', `<${e.target.value}>`); }}>
            {BLOCKS.map((b) => <option key={b.v} value={b.v}>{b.t}</option>)}
          </select>
          {/* Font, size, colour and alignment are the legacy HtmlEditor's
              typesetting controls. A prose field does not need them — and every
              one offered is another way to produce a description that does not
              match the rest of the document. */}
          {!simple && (
            <>
              <select className="rte-font" title="Font name" value={font} onChange={(e) => { const v = e.target.value; setFont(v); applyStyle('fontName', v); }}>
                {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
              {showSizesPx ? (
                <select className="rte-size" title="Font size" defaultValue="" onChange={(e) => { const v = e.target.value; e.currentTarget.selectedIndex = 0; applyFontSizePx(v); }}>
                  <option value="" disabled>Size</option>
                  {PX_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <select className="rte-size" title="Font size" defaultValue="" onChange={(e) => { const v = e.target.value; e.currentTarget.selectedIndex = 0; applyStyle('fontSize', v); }}>
                  <option value="" disabled>(Font Size)</option>
                  {SIZES.map((s) => <option key={s.v} value={s.v}>{s.t}</option>)}
                </select>
              )}
              {wordToolbar && (
                <>
                  <button type="button" className="rte-btn rte-btn-txt" title="Increase font size" onMouseDown={(e) => { e.preventDefault(); stepFontSize(1); }}>A+</button>
                  <button type="button" className="rte-btn rte-btn-txt" title="Decrease font size" onMouseDown={(e) => { e.preventDefault(); stepFontSize(-1); }}>A&#8722;</button>
                  <button type="button" className="rte-btn" title="Change case" onMouseDown={(e) => { e.preventDefault(); changeCase(); }}><i className="ti ti-letter-case" /></button>
                </>
              )}
              {wordToolbar ? (
                <>
                  <RteColorMenu icon="ti-letter-a" title="Font colour" autoLabel="Automatic" autoValue="#000000" value={foreColor} onPick={pickFore} />
                  <RteColorMenu icon="ti-highlight" title="Text highlight colour" autoLabel="No Color" autoValue="transparent" value={hiliteColor} onPick={pickHilite} />
                </>
              ) : showSwatches ? (
                <span className="rte-swatches" title="Font colour">
                  {SWATCHES.map((c) => (
                    <button key={c} type="button" className="rte-swatch" style={{ background: c }} title={c}
                      onMouseDown={(e) => { e.preventDefault(); applyStyle('foreColor', c); }} />
                  ))}
                </span>
              ) : (
                <input type="color" className="rte-color" title="Text colour" defaultValue="#000000" onChange={(e) => { const v = e.target.value; applyStyle('foreColor', v); }} />
              )}
            </>
          )}
          <span className="rte-sep" />
          {btn('bold', 'ti-bold', 'Bold')}
          {btn('italic', 'ti-italic', 'Italic')}
          {btn('underline', 'ti-underline', 'Underline')}
          {!simple && btn('strikeThrough', 'ti-strikethrough', 'Strikethrough')}
          {wordToolbar && btn('subscript', 'ti-subscript', 'Subscript')}
          {wordToolbar && btn('superscript', 'ti-superscript', 'Superscript')}
          {!simple && (
            <>
              <span className="rte-sep" />
              {btn('justifyLeft', 'ti-align-left', 'Align left')}
              {btn('justifyCenter', 'ti-align-center', 'Align center')}
              {btn('justifyRight', 'ti-align-right', 'Align right')}
              {wordToolbar && btn('justifyFull', 'ti-align-justified', 'Justify')}
            </>
          )}
          <span className="rte-sep" />
          {btn('insertUnorderedList', 'ti-list', 'Bullet list')}
          {btn('insertOrderedList', 'ti-list-numbers', 'Numbered list')}
          {wordToolbar && (
            <button type="button" className="rte-btn" title="Multilevel list" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}><i className="ti ti-list-tree" /></button>
          )}
          {btn('outdent', 'ti-indent-decrease', 'Decrease indent')}
          {btn('indent', 'ti-indent-increase', 'Increase indent')}
          {wordToolbar && (
            <select className="rte-size" title="Line spacing" defaultValue="" onChange={(e) => { const v = e.target.value; e.currentTarget.selectedIndex = 0; setLineHeight(v); }}>
              <option value="" disabled>↕</option>
              {['1.0', '1.15', '1.5', '2.0'].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          <span className="rte-sep" />
          <button type="button" className="rte-btn" title="Insert link" onMouseDown={(e) => { e.preventDefault(); const url = window.prompt('Link URL:'); if (url) exec('createLink', url); }}><i className="ti ti-link" /></button>
          {simple ? (
            <>
              <button type="button" className="rte-btn" title="Insert image"
                      onMouseDown={(e) => { e.preventDefault(); const url = window.prompt('Image URL:'); if (url) exec('insertImage', url); }}>
                <i className="ti ti-photo" />
              </button>
              <button type="button" className="rte-btn" title="Insert table"
                      onMouseDown={(e) => { e.preventDefault(); insertTable(); }}>
                <i className="ti ti-table" />
              </button>
              <span className="rte-sep" />
              {btn('undo', 'ti-arrow-back-up', 'Undo')}
              {btn('redo', 'ti-arrow-forward-up', 'Redo')}
            </>
          ) : btn('removeFormat', 'ti-clear-formatting', 'Clear formatting')}
          {showUndo && !simple && (
            <>
              <span className="rte-sep" />
              {btn('undo', 'ti-arrow-back-up', 'Undo')}
              {btn('redo', 'ti-arrow-forward-up', 'Redo')}
            </>
          )}
        </div>
      )}

      {mode === 'design' && (
        <div ref={ref} className="rte-area" contentEditable suppressContentEditableWarning
          onInput={() => { emit(); saveSel(); }}
          onMouseUp={saveSel} onKeyUp={saveSel} />
      )}
      {mode === 'html' && <textarea className="rte-area rte-html" value={value} onChange={(e) => onChange(e.target.value)} />}
      {mode === 'preview' && (renderPreview
        ? <div className="rte-area rte-preview">{renderPreview()}</div>
        : <div className="rte-area rte-preview" dangerouslySetInnerHTML={{ __html: value }} />)}

      {/* The source tabs are for hand-tuning markup, which is not what a prose
          field is for — and "HTML" invites pasting markup the form then has to
          trust. */}
      {!simple && !hideSourceTabs && (
        <div className="rte-modes">
          {(['design', 'html', 'preview'] as Mode[]).map((m) => (
            <button key={m} type="button" className={`rte-mode${mode === m ? ' active' : ''}`} onClick={() => setMode(m)}>
              {m === 'design' ? 'Design' : m === 'html' ? 'HTML' : 'Preview'}
            </button>
          ))}
        </div>
      )}

      {(footerNote || showWordCount) && (
        <div className="rte-foot">
          <span>{footerNote}</span>
          {showWordCount && (
            <span className="rte-wc">{wordCount(value)} words</span>
          )}
        </div>
      )}
    </div>
  );
}
